// What a puzzle constrains on its board, by the solver's status: the puzzles
// plugin is the constraints' owner and updates them as the puzzle goes on.
// Pure, so the mapping is tested without a board.
import type { Constraint } from '@chess/api-constraints';
import type { SolveStatus } from './solving.ts';

/** The giveaways hidden while the answer is still to find. */
function hidden(puzzlePly: number): Constraint[] {
  return [
    { type: 'scoresheetVisibility', hideFrom: puzzlePly },
    { type: 'bestMovesVisibility', visible: false },
    { type: 'boardArrowsVisibility', visible: false },
    { type: 'scoreBarVisibility', visible: false },
  ];
}

/**
 * The constraints for a puzzle in a status. `head` is the ply of the board's
 * own position along its path (the puzzle's position, or as far as its line
 * has come): the controls may look back to the start and forward to it, never
 * past it, since the moves ahead are the answer. `playingOut` while the line
 * plays out after the puzzle is solved (`Solving.playOut`).
 */
export function puzzleConstraints(status: SolveStatus, puzzlePly: number, head: number, playingOut = false): Constraint[] {
  // Solved, the line playing out after the reader's move: nothing moves but
  // the puzzle until its last move; the giveaways are back, the answer found.
  if (status === 'solved' && playingOut) {
    return [
      { type: 'controls', back: false, forward: false, scoresheetClicks: false },
      { type: 'nextMove', allow: 'none' },
      { type: 'bestMovesVisibility', visible: false },
    ];
  }
  switch (status) {
    case 'playing':
      return [
        { type: 'controls', back: true, forward: true, range: { from: 0, to: head }, scoresheetClicks: false },
        { type: 'nextMove', allow: 'judged' },
        ...hidden(puzzlePly),
      ];
    // The engine is answering, a wrong move waits to be taken back, or the
    // solution plays itself: nothing moves but the puzzle.
    case 'thinking':
    case 'failed':
    case 'showing':
      return [
        { type: 'controls', back: false, forward: false, scoresheetClicks: false },
        { type: 'nextMove', allow: 'none' },
        ...hidden(puzzlePly),
      ];
    // Solved, or the solution shown: the board is free again and the giveaways
    // come back. The card stays in the best-moves place until "Back to the game".
    case 'solved':
    case 'shown':
      return [{ type: 'bestMovesVisibility', visible: false }];
  }
}
